import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ukWhen } from "@/lib/time";
import { toast } from "sonner";
import { Btn, CellMain, CellSub, Empty, Panel, St } from "@/components/portal/crm/kit";

/**
 * The client relay, desk side.
 *
 * A desk raises "ask the client X"; it lands here. One tap sends it — as written, no editing, which
 * is the owner's own rule: the desk is a post box, not a sub-editor. A question that shouldn't go
 * is **held back** visibly instead, so the desk that asked can see it didn't go.
 */

const TONE = { raised: "amber", sent: "blue", answered: "green", cancelled: "red" };
const LABEL = { raised: "Waiting to go", sent: "With the client", answered: "Answered", cancelled: "Held back" };

const Ask = ({ a, onSend, onHold, busy, showCase }) => (
  <div className="opt" style={{ display: "block" }} data-testid={`client-ask-${a.id}`}>
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        {showCase && a.case && (
          <CellSub style={{ margin: "0 0 5px" }}>
            <Link to={`/hub/leads/${a.lead_id}`} data-testid={`client-ask-case-${a.id}`}
                  className="font-semibold text-[#0044CC]">
              {a.case.name}
            </Link>
            {a.case.reference ? ` · ${a.case.reference}` : ""}
          </CellSub>
        )}
        <CellMain style={{ fontSize: 14, lineHeight: 1.45 }}>&ldquo;{a.question}&rdquo;</CellMain>
        <CellSub>
          {a.raised_by_name} · {ukWhen(a.created_at, "—")}
          {a.answered_at ? ` · answered ${ukWhen(a.answered_at, "")}` : a.sent_at ? ` · sent ${ukWhen(a.sent_at, "")}` : ""}
        </CellSub>
        {a.answer && (
          <div style={{ marginTop: 9, padding: "10px 13px", borderLeft: "3px solid var(--green)",
            background: "rgba(13,148,136,.06)", borderRadius: 8, fontSize: 13.5, color: "var(--ink)" }}
               data-testid={`client-ask-answer-${a.id}`}>
            {a.answer}
          </div>
        )}
        {a.status === "cancelled" && a.held_reason && (
          <CellSub style={{ marginTop: 7 }}>Held back: {a.held_reason}</CellSub>
        )}
      </div>
      <St tone={TONE[a.status] || "slate"} testId={`client-ask-state-${a.id}`}>{LABEL[a.status] || a.status}</St>
    </div>

    {a.status === "raised" && (
      <div style={{ display: "flex", gap: 9, marginTop: 12, flexWrap: "wrap" }}>
        <Btn kind="primary" sm onClick={() => onSend(a)} disabled={busy === a.id || (a.case && a.case.has_email === false)}
             testId={`client-ask-send-${a.id}`}>
          {busy === a.id ? "Sending…" : "Send it to the client"}
        </Btn>
        <Btn sm onClick={() => onHold(a)} disabled={busy === a.id} testId={`client-ask-hold-${a.id}`}>
          Hold it back
        </Btn>
        {a.case && a.case.has_email === false && (
          <CellSub style={{ margin: "7px 0 0" }}>No email on file for this client — ring them instead.</CellSub>
        )}
      </div>
    )}
  </div>
);

/** Every question on one case, with the desk's send button. */
export function ClientAsksPanel({ leadId, onChanged, testId = "hub-client-asks" }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);

  const load = async () => {
    try { const r = await api.get("/hub/client-asks", { params: { lead_id: leadId, limit: 50 } }); setItems(r.data.items || []); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [leadId]);

  const send = async (a) => {
    setBusy(a.id);
    try { await api.post(`/hub/client-asks/${a.id}/send`); toast.success("Sent — the client answers on a private link"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't send"); }
    finally { setBusy(null); }
  };

  const hold = async (a) => {
    setBusy(a.id);
    try { await api.post(`/hub/client-asks/${a.id}/hold`, { reason: "The desk was answered directly" }); toast.success("Held back — nothing went to the client"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't hold that"); }
    finally { setBusy(null); }
  };

  const ask = async () => {
    if (q.trim().length < 8) { toast.error("Write the question out in full"); return; }
    setAsking(true);
    try {
      await api.post(`/hub/leads/${leadId}/client-ask`, { question: q.trim() });
      toast.success("Sent to the client");
      setQ(""); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't send"); }
    finally { setAsking(false); }
  };

  return (
    <Panel title="Questions put to the client"
           sub="A desk asks, you send it, the answer lands on this thread. The client only ever hears from Connexis."
           testId={testId}>
      {items === null ? (
        <Empty testId={`${testId}-loading`}>Reading the relay…</Empty>
      ) : items.length === 0 ? (
        <div data-testid={`${testId}-empty`}>
          <CellSub style={{ margin: 0 }}>
            Nothing asked yet. A desk can raise a question from their own case record, or ask one yourself below.
          </CellSub>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((a) => <Ask key={a.id} a={a} onSend={send} onHold={hold} busy={busy} />)}
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <textarea className="inp" value={q} onChange={(e) => setQ(e.target.value)} maxLength={600}
                  placeholder="Ask the client something yourself — it goes out from Connexis straight away…"
                  data-testid={`${testId}-question`} />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 11 }}>
          <Btn kind="primary" onClick={ask} disabled={asking || q.trim().length < 8} testId={`${testId}-ask`}>
            {asking ? "Sending…" : "Ask the client"}
          </Btn>
        </div>
      </div>
    </Panel>
  );
}

/** The desk-wide queue — questions from desks that nobody has sent on yet. */
export function ClientAsksCard({ testId = "hub-relay-queue" }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const r = await api.get("/hub/client-asks", { params: { status: "raised", limit: 20 } }); setItems(r.data.items || []); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const send = async (a) => {
    setBusy(a.id);
    try { await api.post(`/hub/client-asks/${a.id}/send`); toast.success("Sent — the client answers on a private link"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't send"); }
    finally { setBusy(null); }
  };

  const hold = async (a) => {
    setBusy(a.id);
    try { await api.post(`/hub/client-asks/${a.id}/hold`, { reason: "Held back by the desk" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't hold that"); }
    finally { setBusy(null); }
  };

  if (items !== null && items.length === 0) return null;   // a clean desk shows nothing

  return (
    <Panel title="Questions waiting to go to a client"
           sub="A desk has asked something. Send it as written — the answer comes back on the case thread."
           testId={testId}>
      {items === null ? (
        <Empty testId={`${testId}-loading`}>Reading the relay…</Empty>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((a) => <Ask key={a.id} a={a} onSend={send} onHold={hold} busy={busy} showCase />)}
        </div>
      )}
    </Panel>
  );
}
